import { Button } from '@shared/components/ui/button';
import { Combobox } from '@shared/components/ui/AdvancedFilter/components';
import { Input } from '@shared/components/ui/input';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@shared/components/ui/shadcn/dialog';
import { useToast } from '@shared/hooks/use-toast';
import { useAuthStore } from '@stores/authStore';
import { Plus, Save } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useCreateDefaultShippingCost,
  useProfitabilityCourierServiceOptions,
  useUpdateDefaultShippingCost,
} from '../../hooks';
import { getProfitabilityMutationErrorMessage } from '../../utils/profitabilityApiError';

export type DefaultShippingCostFormModalMode = 'create' | 'edit';

export interface DefaultShippingCostFormModalTarget {
  mode: DefaultShippingCostFormModalMode;
  id?: number;
  team_id: number;
  team_name?: string;
  courier_service_id?: number;
  serviceLabel?: string;
  cost?: number;
  cod_fee_fixed?: number;
  cod_fee_percent?: number;
  /** Permite elegir servicio (alta manual desde Configurados). */
  allowServiceSelect?: boolean;
  /** Texto contextual bajo el servicio (p. ej. missing). */
  hint?: string;
  /** Tras guardar, volver a la pestaña Sin coste. */
  returnToMissingOnSave?: boolean;
}

interface DefaultShippingCostFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: DefaultShippingCostFormModalTarget | null;
  onSaved?: () => void;
}

function parseOptionalNonNegative(raw: string): { ok: true; value: number } | { ok: false } {
  const trimmed = raw.trim();
  if (trimmed === '') return { ok: true, value: 0 };
  const n = parseFloat(trimmed.replace(',', '.'));
  if (!Number.isFinite(n) || n < 0) return { ok: false };
  return { ok: true, value: n };
}

export const DefaultShippingCostFormModal = ({
  open,
  onOpenChange,
  target,
  onSaved,
}: DefaultShippingCostFormModalProps) => {
  const { t } = useTranslation('profitabilityModule');
  const { toast } = useToast();
  const team = useAuthStore((s) => s.team);
  const createM = useCreateDefaultShippingCost();
  const updateM = useUpdateDefaultShippingCost();
  const courierOptionsQ = useProfitabilityCourierServiceOptions();

  const [formCourierServiceId, setFormCourierServiceId] = useState('');
  const [formCost, setFormCost] = useState('');
  const [formCodFeeFixed, setFormCodFeeFixed] = useState('');
  const [formCodFeePercent, setFormCodFeePercent] = useState('');

  const isEdit = target?.mode === 'edit' && target.id != null;
  const showServiceSelect = target?.mode === 'create' && target.allowServiceSelect === true;
  const showServiceInfo = !showServiceSelect && (target?.serviceLabel || target?.courier_service_id);

  const resetForm = useCallback(() => {
    setFormCourierServiceId('');
    setFormCost('');
    setFormCodFeeFixed('');
    setFormCodFeePercent('');
  }, []);

  useEffect(() => {
    if (!open || !target) {
      if (!open) resetForm();
      return;
    }

    setFormCourierServiceId(target.courier_service_id != null ? String(target.courier_service_id) : '');
    setFormCost(target.cost != null ? String(target.cost) : '');
    setFormCodFeeFixed(target.cod_fee_fixed != null ? String(target.cod_fee_fixed) : '');
    setFormCodFeePercent(target.cod_fee_percent != null ? String(target.cod_fee_percent) : '');
  }, [open, target, resetForm]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!target) return;

    const team_id = target.team_id ?? team?.id;
    if (team_id == null || team_id < 1) {
      toast({
        title: t('default_shipping_costs.toast.validation'),
        description: t('default_shipping_costs.validation_no_team'),
        variant: 'destructive',
      });
      return;
    }

    const courier_service_id = Number(formCourierServiceId);
    const cost = parseFloat(formCost.replace(',', '.'));
    if (!Number.isFinite(courier_service_id) || courier_service_id < 1) {
      toast({
        title: t('default_shipping_costs.toast.validation'),
        description: t('default_shipping_costs.validation_service'),
        variant: 'destructive',
      });
      return;
    }
    if (!Number.isFinite(cost) || cost < 0) {
      toast({
        title: t('default_shipping_costs.toast.validation'),
        description: t('default_shipping_costs.validation_cost'),
        variant: 'destructive',
      });
      return;
    }

    const codFixed = parseOptionalNonNegative(formCodFeeFixed);
    if (!codFixed.ok) {
      toast({
        title: t('default_shipping_costs.toast.validation'),
        description: t('default_shipping_costs.validation_cod_fee_fixed'),
        variant: 'destructive',
      });
      return;
    }
    const codPercent = parseOptionalNonNegative(formCodFeePercent);
    if (!codPercent.ok) {
      toast({
        title: t('default_shipping_costs.toast.validation'),
        description: t('default_shipping_costs.validation_cod_fee_percent'),
        variant: 'destructive',
      });
      return;
    }

    const payload = {
      team_id,
      courier_service_id,
      cost,
      cod_fee_fixed: codFixed.value,
      cod_fee_percent: codPercent.value,
    };

    try {
      if (isEdit && target.id != null) {
        await updateM.mutateAsync({ id: target.id, body: payload });
        toast({ title: t('default_shipping_costs.toast.updated') });
      } else {
        await createM.mutateAsync(payload);
        toast({ title: t('default_shipping_costs.toast.created') });
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast({
        title: t('default_shipping_costs.toast.error'),
        description: getProfitabilityMutationErrorMessage(err, t('errors.generic')),
        variant: 'destructive',
      });
    }
  };

  const saving = createM.isPending || updateM.isPending;
  const courierOptions = courierOptionsQ.data ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg border-border bg-background"
        data-testid="profitability-default-shipping-form-modal"
      >
        <DialogHeader>
          <DialogTitle>
            {isEdit ? t('default_shipping_costs.form_edit_title') : t('default_shipping_costs.form_add_title')}
          </DialogTitle>
        </DialogHeader>

        {showServiceInfo && target && (
          <div className="space-y-1 rounded-lg border border-border bg-muted/20 p-3 text-sm">
            <p className="font-medium text-foreground">{target.serviceLabel ?? `#${target.courier_service_id}`}</p>
            {target.team_name && (
              <p className="text-muted-foreground">
                {t('default_shipping_costs.configure_modal_team', { team: target.team_name })}
              </p>
            )}
            {target.hint && <p className="text-xs text-muted-foreground">{target.hint}</p>}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            {showServiceSelect && (
              <div className="space-y-1 sm:col-span-2">
                <label className="text-xs font-medium text-muted-foreground">
                  {t('default_shipping_costs.field_service')}
                </label>
                <Combobox
                  options={courierOptions.map((o) => ({ label: o.label, value: o.value }))}
                  value={formCourierServiceId}
                  setValue={setFormCourierServiceId}
                  placeholder={t('default_shipping_costs.field_service_placeholder')}
                  disabled={courierOptionsQ.isLoading}
                  dataTestId="profitability-default-shipping-form-modal-service"
                />
              </div>
            )}
            <div className="space-y-1 sm:col-span-2">
              <label className="text-xs font-medium text-muted-foreground">
                {t('default_shipping_costs.field_cost')}
              </label>
              <Input
                type="text"
                inputMode="decimal"
                value={formCost}
                onChange={(e) => setFormCost(e.target.value)}
                placeholder="0.00"
                className="border-border bg-background"
                autoFocus={!showServiceSelect}
                data-testid="profitability-default-shipping-form-modal-cost"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">
                {t('default_shipping_costs.field_cod_fee_fixed')}
              </label>
              <Input
                type="text"
                inputMode="decimal"
                value={formCodFeeFixed}
                onChange={(e) => setFormCodFeeFixed(e.target.value)}
                placeholder="0"
                className="border-border bg-background"
                data-testid="profitability-default-shipping-form-modal-cod-fee-fixed"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">
                {t('default_shipping_costs.field_cod_fee_percent')}
              </label>
              <Input
                type="text"
                inputMode="decimal"
                value={formCodFeePercent}
                onChange={(e) => setFormCodFeePercent(e.target.value)}
                placeholder="0"
                className="border-border bg-background"
                data-testid="profitability-default-shipping-form-modal-cod-fee-percent"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:justify-start">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-border">
              {t('default_shipping_costs.delete_cancel')}
            </Button>
            <Button type="submit" disabled={saving || !target} className="gap-2">
              {isEdit ? <Save className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
              {isEdit ? t('default_shipping_costs.save') : t('default_shipping_costs.add')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
